import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { RootState, StoreState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { messagingApi } from "messaging/_api/messaging-api";
import Message, { inReadingOrder } from "messaging/message";
import { isUnreadBy } from "messaging/read-receipts/unread-rule";

const initialState: StoreState<PaginatedResponse<Message>> = {
  entries: {},
  loading: {},
  error: {},
};

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Message>) => {
      const forumId = action.payload.forum.id?.value;
      if (!forumId) return;

      let existingForumMessages = state.entries[forumId];

      if (!existingForumMessages) {
        existingForumMessages = PaginatedResponse.empty<Message>();
      }

      existingForumMessages = existingForumMessages.appendOrReplaceData([action.payload], undefined, inReadingOrder);
      state.entries[forumId] = existingForumMessages;
    },
    removeMessage: (state, action: PayloadAction<Guid>) => {
      for (const [forumId, forumMessages] of Object.entries(state.entries)) {
        if (!forumMessages) continue;
        state.entries[forumId] = forumMessages.removeDataById(action.payload);
      }
    },
    clearMessages: (state, action: PayloadAction<Guid>) => {
      const forumId = action.payload.value;
      if (!forumId) return;

      state.entries[forumId] = PaginatedResponse.empty<Message>();
      state.loading[forumId] = false;
      state.error[forumId] = null;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(messagingApi.endpoints.getMessagesByForum.matchPending, (state, action) => {
      const forumId = action.meta.arg.originalArgs?.forumId;
      if (!forumId) return;

      state.loading[forumId] = true;
      state.error[forumId] = null;
    });
    builder.addMatcher(messagingApi.endpoints.getMessagesByForum.matchFulfilled, (state, action) => {
      const { forumId, append } = action.meta.arg.originalArgs;
      if (!forumId) return;

      // Kept in reading order whichever way the page came: a page that replaces the list is sorted as
      // surely as one merged into it, so the conversation reads the same on a fresh load and a refetch.
      const existingForumMessages = state.entries[forumId] ?? PaginatedResponse.empty<Message>();
      const base = !existingForumMessages.isEmpty && append ? existingForumMessages : PaginatedResponse.empty<Message>();
      state.entries[forumId] = base.appendOrReplaceData(action.payload.data, action.payload.headers, inReadingOrder);
      state.loading[forumId] = false;
    });
    builder.addMatcher(messagingApi.endpoints.getMessagesByForum.matchRejected, (state, action) => {
      const forumId = action.meta.arg.originalArgs?.forumId;
      if (!forumId) return;

      state.loading[forumId] = false;
      state.error[forumId] = action.error;
    });
    builder.addMatcher(messagingApi.endpoints.createMessage.matchFulfilled, (state, action) => {
      upsertMessage(state, action.payload);
    });
    builder.addMatcher(messagingApi.endpoints.deleteMessage.matchFulfilled, (state, action) => {
      upsertMessage(state, action.payload);
    });
    builder.addMatcher(messagingApi.endpoints.updateMessage.matchFulfilled, (state, action) => {
      upsertMessage(state, action.payload);
    });
    builder.addMatcher(messagingApi.endpoints.createBulkMessages.matchFulfilled, (state, action) => {
      action.payload.forEach((message) => upsertMessage(state, message));
    });
    // The server marked every unread message in the forum read, and answers with a count rather than
    // the messages. The ones on screen are drawn read here, so Mark all read clears them without
    // fetching every page again.
    builder.addMatcher(messagingApi.endpoints.markForumRead.matchFulfilled, (state, action) => {
      const { forumId, readerId } = action.meta.arg.originalArgs;
      markLoadedMessagesRead(state, forumId, readerId);
    });
  },
});

function upsertMessage(state: { entries: Record<string, unknown> }, message: Message) {
  const forumId = message.forum.id?.value;
  if (!forumId) return;

  const existingForumMessages =
    (state.entries[forumId] as PaginatedResponse<Message> | undefined) ?? PaginatedResponse.empty<Message>();
  state.entries[forumId] = existingForumMessages.appendOrReplaceData([message], undefined, inReadingOrder);
}

function markLoadedMessagesRead(state: { entries: Record<string, unknown> }, forumId: string, readerId: Guid) {
  const forumMessages = state.entries[forumId] as PaginatedResponse<Message> | undefined;
  if (!forumMessages) return;

  const nowRead = forumMessages.data
    .filter((message) => isUnreadBy(message, readerId))
    .map((message) => message.readBy(readerId));
  if (nowRead.length > 0) state.entries[forumId] = forumMessages.appendOrReplaceData(nowRead);
}

const selectEntries = (state: RootState) => state.messages.entries;

const selectMessagesByForumId = createSelector(
  [selectEntries, (_: RootState, forumId?: Guid) => forumId?.value ?? ""],
  (entries, id) => entries[id] ?? []
);

export const { addMessage, removeMessage, clearMessages } = messagesSlice.actions;

export const getIsLoadingMessagesByForumId = (forumId?: Guid) => (state: RootState) => {
  if (!forumId) return false;
  return state.messages?.loading[forumId.value];
};
export const getMessagesByForumId = (forumId?: Guid) => (state: RootState) => selectMessagesByForumId(state, forumId);

export const getErrorLoadingMessagesByForumId = (forumId?: Guid) => (state: RootState) =>
  state.messages.error[forumId?.value ?? ""];

export const getMessageHasImagesAttached = (message: Message) => {
  return message.attachments.some((attachment) => attachment.isImage);
};

export default messagesSlice;
